
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { Observable, firstValueFrom } from 'rxjs';
import { ISearch, ISearchResult, Payer, RestrictedAnalytesLookup, SearchLookupData } from '../models';
import { GlobalSearch } from '../models/results/global-search';
import { AppState } from '../store/reducers';
import { selectResultsState } from '../store/reducers/results.reducer';
import { ConfigService } from './config.service';

@Injectable()
export class ResultsService {

  basePath?: string;
  uoUrl?: string;
  searchUrl?: string;
  downloadUrl?: string;

  payerSearchQueryPath?: string;
  payerSearchGlobalPath?: string;
  payerSearchResultPath?: string;
  payerSearchFTP?: string;
  restrictedAnalytesLookup: RestrictedAnalytesLookup[];

  constructor(private readonly configService: ConfigService, private readonly httpClient: HttpClient, private readonly store: Store<AppState>) {

    this.uoUrl = this.configService.appConfig.api.uoUrl;
    this.searchUrl = this.configService.appConfig.api.searchUrl;

    this.downloadUrl = this.configService.appConfig.api.downloadUrl;

    this.payerSearchQueryPath = `${this.searchUrl}/query`;
    this.payerSearchGlobalPath = `${this.searchUrl}/global`;
    this.payerSearchResultPath = `${this.searchUrl}/result`;
    this.payerSearchFTP = `${this.searchUrl}/ftp`;


    store.select(selectResultsState).pipe(takeUntilDestroyed()).subscribe(resultsState => {
      if (resultsState) {
        this.restrictedAnalytesLookup = resultsState.restrictedAnalytesLookup;
      }
    });
  }

  getSavedSearches(): Promise<ISearch[]> {
    return firstValueFrom(this.httpClient.get<ISearch[]>(`${this.payerSearchQueryPath}`));
  }

  getFTPSavedSearches(): Promise<ISearch[]> {
    return firstValueFrom(this.httpClient.get<ISearch[]>(`${this.payerSearchFTP}`));
  }

  getFTPSearchLookupData(orgId: string): Promise<SearchLookupData> {

    // If there is an orgId, it's a saved search and the service returns the org for that orgId.
    // If there is an orgId, the service returns the org for that orgId.
    // If there is NOT an orgId, the service returns all orgs for the user.

    if (orgId !== "") orgId = `/${orgId}`;
    const accountsPromise = firstValueFrom(this.httpClient.get<Payer[]>(`${this.uoUrl}/org/payerAccounts${orgId}`));
    const searchLookupPromise = firstValueFrom(this.httpClient.get<SearchLookupData>(`${this.payerSearchFTP}/lookup`));

    return Promise
      .all([accountsPromise, searchLookupPromise])
      .then(results => {
        const searchLookupData = results[1];
        searchLookupData.accounts = results[0].filter(a => a.ftpEnabled);
        return searchLookupData;
      });
  }

  getSearchLookupData(): Promise<SearchLookupData> {

    return firstValueFrom(this.httpClient.get<SearchLookupData>(`${this.payerSearchQueryPath}/lookup`)).then((searchLookupData) => {
      return firstValueFrom(this.httpClient.get<Payer[]>(`${this.uoUrl}/org/payerAccounts`)).then((payers) => {
        searchLookupData.accounts = payers;
        return searchLookupData;
      });
    });

  }

  getGlobalLookupData(): Promise<SearchLookupData> {
    return firstValueFrom(this.httpClient.get<SearchLookupData>(`${this.payerSearchGlobalPath}/lookup`));
  }

  getRestrictedAnalytesLookup(): Promise<RestrictedAnalytesLookup[]> {
    return firstValueFrom(this.httpClient.get<RestrictedAnalytesLookup[]>(`${this.uoUrl}/search/restrictedAnalytes`));
  }

  search(searchData: ISearch): Promise<any> {
    const url = searchData.query.fileType ==='pdf' ? `${this.payerSearchQueryPath}/pdf` : `${this.payerSearchQueryPath}/adhoc`;
    return firstValueFrom(this.httpClient.post(url, searchData, { observe: 'response' })).then(response => response).catch(response => response);
  }

  globalSearch(searchData: GlobalSearch): Promise<any> {
    const url = searchData.fileType ==='pdf' ? `${this.payerSearchGlobalPath}/pdf` : `${this.payerSearchGlobalPath}/adhoc`;
    return firstValueFrom(this.httpClient.post(url, searchData, { observe: 'response' })).then(response => response)
      .catch(response => response);
  }

  preview(searchData: ISearch): Promise<any> {
    return firstValueFrom(this.httpClient
      .post(`${this.payerSearchQueryPath}/preview`, searchData, { observe: 'response' })).then(response => response).catch(response => response);
  }

  globalPreview(searchData: GlobalSearch): Promise<any> {
    return firstValueFrom(this.httpClient.post(`${this.payerSearchGlobalPath}/preview`, searchData, { observe: 'response' })).then(response => response).catch(response => response);
  }

  saveSearch(searchData: ISearch): Promise<ISearch> {
    return firstValueFrom(this.httpClient.post<ISearch>(`${this.payerSearchQueryPath}/save`, searchData)).then(response => response).catch(response => response);
  }

  deleteSearch(queryId: number): Promise<any> {
    return firstValueFrom(this.httpClient.delete(`${this.payerSearchQueryPath}/${queryId}`));
  }

  getSearchResult(encryptedResultId: string): Promise<any> {
    return firstValueFrom(this.httpClient.get(`${this.payerSearchResultPath}/${encryptedResultId}`)).then(response => response).catch(response => response);
  }

  getSavedSearch(queryId: string): Promise<ISearch> {
    return firstValueFrom(this.httpClient.get<ISearch>(`${this.payerSearchQueryPath}/${queryId}`)).then(response => response).catch(response => response);
  }

  getSearchResults(): Promise<ISearchResult[]> {
    return firstValueFrom(this.httpClient.get<ISearchResult[]>(`${this.payerSearchResultPath}`)).then(response => response).catch(response => response);
  }

  deleteSearchResult(resultId: number): Observable<any> {
    return this.httpClient.delete(`${this.payerSearchResultPath}/${resultId}`);
  }

  downloadSearchResult(resultId: number): Promise<any> {
    return firstValueFrom(this.httpClient.get(`${this.downloadUrl}/search/result/rid/${resultId}`, { responseType: 'blob' }));
  }

  getSearchBasedOnEncryptedQueryId(encryptedQueryId: string): Promise<ISearch> {
    return firstValueFrom(this.httpClient.get<ISearch>(`${this.payerSearchQueryPath}/${encryptedQueryId}`));
  }

}
